import React, { useCallback, useEffect, useState } from 'react';
import { Link2, RefreshCw, Settings, Copy, MapPin, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { AgencyGHLDialog } from './AgencyGHLDialog';
import './agency-ghl.css';

const HEALTH = {
  connected: 'Connected', not_connected: 'Not connected', not_checked: 'Not checked yet',
  reconnect_required: 'Reconnect required', permission_denied: 'Permission needed',
  location_unavailable: 'Location unavailable', unavailable: 'Temporarily unavailable',
};
const CALLBACK_MESSAGES = {
  connected: 'Agency GHL destination connected', check_required: 'Destination saved. Check its connection status.',
  denied: 'GHL authorization cancelled. Your saved destination was not changed.',
  expired: 'The connection request expired. Start again from Integrations.',
  changed: 'The connection changed in another tab. Please review its current status.',
  location: 'The selected location did not match. Your previous destination was preserved.',
  failed: 'GHL authorization could not be completed. Please try connecting again.',
};

export const AgencyGHLCard = ({ api, workspaceId, onStatusChange }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const base = `/api/workspaces/${workspaceId}/integrations/ghl-agency`;
  const receive = useCallback(value => { setData(value); onStatusChange?.(value); }, [onStatusChange]);
  const load = useCallback(async () => {
    setError('');
    try { const r = await api.get(`${base}/status`); receive(r.data); }
    catch { setError('Could not load the agency GHL connection.'); }
  }, [api, base, receive]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('agency_ghl');
    if (!result) return;
    const message = CALLBACK_MESSAGES[result];
    if (message) result === 'connected' ? toast.success(message) : toast.info(message);
    params.delete('agency_ghl');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    load();
  }, [location.pathname, location.search, navigate, load]);
  const check = async () => {
    setBusy(true); setError('');
    try {
      const r = await api.post(`${base}/check`, { revision: data.revision }); receive(r.data);
      if (r.data.health === 'connected') toast.success('Agency destination connection verified');
    } catch (e) { setError(e.response?.data?.detail || 'Connection check could not be completed.'); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.location_id); toast.success('Location ID copied'); }
    catch { toast.error('Could not copy the location ID'); }
  };
  const healthy = data?.health === 'connected';
  return <section className={`agency-ghl-card ${isDarkMode ? 'agency-ghl-dark' : ''}`} data-testid="integration-gohighlevel-agency">
    <div className="agency-ghl-heading"><div className="agency-ghl-icon"><Link2 size={24}/></div>
      <span className={`agency-ghl-health ${healthy ? 'is-healthy' : ''}`} data-testid="agency-ghl-health" role="status">
        {healthy ? <CheckCircle2 size={12}/> : <span className="agency-ghl-dot"/>}{data ? HEALTH[data.health] || 'Check required' : 'Loading…'}
      </span>
    </div>
    <h3 data-testid="agency-ghl-title">GoHighLevel <span>Agency Leads</span></h3>
    {data?.has_connection ? <div className="agency-ghl-destination" data-testid="agency-ghl-destination">
      <small><MapPin size={12}/> Destination</small>
      <strong data-testid="agency-ghl-location-name">{data.location_name}</strong>
      <div className="agency-ghl-id-row"><code data-testid="agency-ghl-location-id">{data.location_id}</code><button data-testid="agency-ghl-copy-id" title="Copy location ID" aria-label="Copy location ID" onClick={copy}><Copy size={13}/></button></div>
      <p data-testid="agency-ghl-checked-at">{data.last_checked_at ? `Checked ${new Date(data.last_checked_at).toLocaleString()}` : 'Not checked yet'}</p>
      {data.mode === 'canonical_reference' && <p data-testid="agency-ghl-shared-connection">Using an existing workspace connection</p>}
    </div> : <p className="agency-ghl-caption">Agency prospecting destination</p>}
    {data?.message && <p className="agency-ghl-notice" data-testid="agency-ghl-health-message"><AlertCircle size={13}/>{data.message}</p>}
    {error && <p className="agency-ghl-error" role="alert" data-testid="agency-ghl-error">{error} <button data-testid="agency-ghl-retry" onClick={load}>Reload</button></p>}
    <div className="agency-ghl-actions">
      {data?.has_connection && <button className="agency-ghl-secondary" data-testid="agency-ghl-check" onClick={check} disabled={busy || !data.can_manage}><RefreshCw size={14} className={busy ? 'animate-spin' : ''}/>Check connection</button>}
      <button className="agency-ghl-primary" data-testid="agency-ghl-manage" disabled={!data?.can_manage || busy} onClick={() => setOpen(true)}>
        {data?.has_connection ? <Settings size={14}/> : <Link2 size={14}/>} {data?.has_connection ? 'Manage' : 'Connect'}
      </button>
    </div>
    {data && !data.can_manage && <p className="agency-ghl-caption" data-testid="agency-ghl-readonly">Managed by the workspace owner or administrator.</p>}
    {data && <AgencyGHLDialog {...{ api, base, data, open, setOpen, receive, load, workspaceId }} dark={isDarkMode}/>}
  </section>;
};